import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Mail, 
  Smartphone, 
  ShieldCheck, 
  ArrowRight, 
  CheckCircle2,
  Stethoscope,
  User,
  Lock,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface LoginDemoAnimationProps {
  onComplete: () => void;
  onSkip: () => void;
}

const demoSteps = [
  {
    id: 1,
    title: "Enter Your Email or Phone",
    description: "Start by entering your registered email address or phone number",
    icon: Mail,
    secondaryIcon: Smartphone,
    color: "from-primary to-primary/70",
  },
  {
    id: 2,
    title: "Verify Your Identity",
    description: "We'll check if you're registered in our system by your healthcare provider",
    icon: ShieldCheck,
    secondaryIcon: User,
    color: "from-emerald-500 to-emerald-400",
  },
  {
    id: 3,
    title: "Receive Verification Code",
    description: "Once verified, you'll receive a secure 6-digit code via email or SMS",
    icon: Lock,
    secondaryIcon: CheckCircle2,
    color: "from-amber-500 to-amber-400",
  },
  {
    id: 4,
    title: "Access Your Dashboard",
    description: "Enter the code and securely access your personalized health dashboard",
    icon: Stethoscope,
    secondaryIcon: Sparkles,
    color: "from-purple-500 to-purple-400",
  },
];

const LoginDemoAnimation = ({ onComplete, onSkip }: LoginDemoAnimationProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const timer = setTimeout(() => {
      if (currentStep < demoSteps.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        // Auto-complete after last step
        setTimeout(() => {
          onComplete();
        }, 1500);
      }
    }, 2500);

    return () => clearTimeout(timer);
  }, [currentStep, isAutoPlaying, onComplete]);

  const handleStepClick = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentStep(index);
  };

  const handleContinue = () => {
    if (currentStep < demoSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onComplete();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen flex flex-col items-center justify-center bg-background p-4 overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 rounded-full bg-gradient-to-br from-primary/20 to-transparent blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 rounded-full bg-gradient-to-tr from-emerald-500/20 to-transparent blur-3xl"
        />
      </div>

      <div className="w-full max-w-lg relative z-10">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <motion.div
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-16 h-16 gradient-hero rounded-2xl flex items-center justify-center shadow-lg"
            >
              <Stethoscope className="h-9 w-9 text-white" />
            </motion.div>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">How It Works</h1>
          <p className="text-muted-foreground">Quick, secure access to your healthcare</p>
        </motion.div>

        {/* Step Progress Indicators */}
        <div className="flex justify-center gap-2 mb-8">
          {demoSteps.map((step, index) => (
            <button
              key={step.id}
              onClick={() => handleStepClick(index)}
              className={`h-2 rounded-full transition-all duration-500 cursor-pointer ${
                index === currentStep
                  ? "w-8 bg-primary"
                  : index < currentStep
                  ? "w-2 bg-primary/50"
                  : "w-2 bg-muted-foreground/30"
              }`}
            />
          ))}
        </div>

        {/* Animated Step Content */}
        <div className="relative h-[320px]">
          <AnimatePresence mode="wait">
            {demoSteps.map((step, index) =>
              index === currentStep ? (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: 50, scale: 0.95 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -50, scale: 0.95 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="absolute inset-0"
                >
                  <div className="bg-card border border-border rounded-2xl p-8 shadow-lg h-full flex flex-col items-center justify-center">
                    {/* Icon Container */}
                    <div className="relative mb-6">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                        className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}
                      >
                        <step.icon className="h-10 w-10 text-white" />
                      </motion.div>
                      
                      {/* Secondary floating icon */}
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.4, type: "spring" }}
                        className="absolute -right-3 -bottom-3 w-10 h-10 rounded-xl bg-background border-2 border-border flex items-center justify-center shadow-md"
                      >
                        <step.secondaryIcon className="h-5 w-5 text-muted-foreground" />
                      </motion.div>

                      {/* Pulsing ring */}
                      <motion.div
                        animate={{
                          scale: [1, 1.3, 1],
                          opacity: [0.5, 0, 0.5],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeOut",
                        }}
                        className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${step.color} opacity-30`}
                      />
                    </div>

                    {/* Step Number */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="text-sm font-medium text-primary mb-2"
                    >
                      Step {step.id} of {demoSteps.length}
                    </motion.div>

                    {/* Title */}
                    <motion.h2
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="text-xl font-bold text-foreground text-center mb-3"
                    >
                      {step.title}
                    </motion.h2>

                    {/* Description */}
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="text-muted-foreground text-center max-w-sm"
                    >
                      {step.description}
                    </motion.p>
                  </div>
                </motion.div>
              ) : null
            )}
          </AnimatePresence>
        </div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col gap-3 mt-8"
        >
          <Button
            onClick={handleContinue}
            size="lg"
            className="w-full gap-2 text-lg h-12"
          >
            {currentStep < demoSteps.length - 1 ? (
              <>
                Next Step
                <ArrowRight className="h-5 w-5" />
              </>
            ) : (
              <>
                Start Login
                <Sparkles className="h-5 w-5" />
              </>
            )}
          </Button>

          <Button
            onClick={onSkip}
            variant="ghost"
            className="text-muted-foreground hover:text-foreground"
          >
            Skip Demo & Login Now
          </Button>
        </motion.div>

        {/* Auto-play indicator */}
        {isAutoPlaying && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-xs text-muted-foreground mt-4"
          >
            Auto-advancing in a moment...
          </motion.p>
        )}
      </div>
    </motion.div>
  );
};

export default LoginDemoAnimation;
